import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import api, { apiError } from '../api';
import { LANGUAGES } from '../languages';
import './HomePage.css';

function formatDate(date) {
  if (!date) return '';
  return new Date(date).toLocaleDateString();
}

function WriterDashboard() {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [roomCode, setRoomCode] = useState('');
  const [language, setLanguage] = useState('English');
  const [customLanguage, setCustomLanguage] = useState('');
  const [sessionsVisible, setSessionsVisible] = useState(true);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const loadSessions = async () => {
    setLoading(true);
    try {
      const response = await api.get('/sessions');
      setSessions(response.data.sessions || []);
    } catch (requestError) {
      setError(apiError(requestError, 'Could not load your sessions.'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadSessions(); }, []);

  const createSession = async () => {
    setBusy(true);
    setError('');
    try {
      const response = await api.post('/sessions');
      navigate(`/session/${response.data.room_code}`);
    } catch (requestError) {
      setError(apiError(requestError, 'Could not create a session.'));
    } finally {
      setBusy(false);
    }
  };

  const joinRoom = (asWriter) => {
    setError('');
    const code = roomCode.trim().toUpperCase();
    if (!/^[A-Z0-9]{6}$/.test(code)) {
      setError('Room code is required.');
      return;
    }
    if (asWriter) {
      navigate(`/session/${code}`);
      return;
    }
    const chosenLanguage = customLanguage.trim() || language;
    if (!chosenLanguage) {
      setError('Language is required to join as a reader.');
      return;
    }
    navigate(`/room/${code}/read?lang=${encodeURIComponent(chosenLanguage)}`);
  };

  const deleteSession = async (code) => {
    if (!window.confirm(`Are you sure you want to permanently delete session ${code}? This cannot be undone.`)) return;
    setBusy(true);
    setError('');
    try {
      await api.delete(`/sessions/${code}`);
      setSessions((current) => current.filter((session) => session.room_code !== code));
    } catch (requestError) {
      setError(apiError(requestError, 'Could not delete that session.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="homepage-container">
      <section className="writer-view" aria-label="Writer home">
        {error && <div className="alert alert-error" role="alert">{error}</div>}

        <div className="action-section create-section">
          <h4>Create a New Session</h4>
          <p>Start a new translation session.</p>
          <button type="button" className="btn btn-primary create-button" onClick={createSession} disabled={busy}>Create New Session</button>
        </div>

        <div className="action-section session-list-section">
          <div className="session-list-header">
            <h4>Your Active Sessions</h4>
            <button type="button" className="toggle-sessions-button" onClick={() => setSessionsVisible((visible) => !visible)}>{sessionsVisible ? 'Hide' : 'Show'}</button>
          </div>
          {sessionsVisible && (loading ? <p role="status">Loading sessions...</p> : sessions.length === 0 ? <p>You have no active sessions.</p> : (
            <ul className="session-list">
              {sessions.map((session) => (
                <li className="session-list-item" key={session.room_code}>
                  <button type="button" className="session-link-button" onClick={() => navigate(`/session/${session.room_code}`)} disabled={busy}>
                    <span className="session-room-code">Room: <strong>{session.room_code}</strong></span>
                    {session.created_at && <span className="session-date">Created: {formatDate(session.created_at)}</span>}
                    {session.first_line && <span className="session-first-line">{session.first_line}</span>}
                  </button>
                  <button type="button" className="delete-session-icon-button" onClick={() => deleteSession(session.room_code)} disabled={busy} aria-label={`Delete session ${session.room_code}`}>{busy ? '...' : '🗑️'}</button>
                </li>
              ))}
            </ul>
          ))}
        </div>

        <div className="action-section join-section">
          <h4>Join Another Session</h4>
          <p>Enter the code and choose how you want to join.</p>
          <div className="input-group">
            <label htmlFor="writer-room-code">Room Code</label>
            <input id="writer-room-code" className="form-control" value={roomCode} onChange={(event) => setRoomCode(event.target.value.toUpperCase())} maxLength={6} autoCapitalize="characters" placeholder="Enter 6-digit code" />
          </div>
          <div className="input-group">
            <label htmlFor="writer-language">Language (for Reader View)</label>
            <select id="writer-language" className="form-control" value={language} onChange={(event) => { setLanguage(event.target.value); setCustomLanguage(''); }} disabled={Boolean(customLanguage.trim())}>
              {LANGUAGES.map((item) => <option key={item} value={item}>{item}</option>)}
            </select>
          </div>
          <div className="input-group">
            <label htmlFor="writer-custom-language">Or Enter Language Name (for Reader View)</label>
            <input id="writer-custom-language" className="form-control" value={customLanguage} onChange={(event) => setCustomLanguage(event.target.value)} placeholder="e.g., Vietnamese (Overrides dropdown)" />
          </div>
          <div className="join-buttons-container">
            <button type="button" className="btn btn-primary" onClick={() => joinRoom(true)}>Join as Writer (Edit)</button>
            <button type="button" className="btn btn-secondary" onClick={() => joinRoom(false)}>Join as Reader (View)</button>
          </div>
        </div>
      </section>
    </div>
  );
}

export default function HomePage({ username }) {
  const navigate = useNavigate();
  const [roomCode, setRoomCode] = useState('');
  const [language, setLanguage] = useState('English');
  const [customLanguage, setCustomLanguage] = useState('');
  const [error, setError] = useState('');

  const joinReader = (event) => {
    event.preventDefault();
    const code = roomCode.trim().toUpperCase();
    if (!/^[A-Z0-9]{6}$/.test(code)) {
      setError('Room code is required.');
      return;
    }
    const chosenLanguage = customLanguage.trim() || language;
    if (!chosenLanguage) {
      setError('Language is required to join as a reader.');
      return;
    }
    navigate(`/room/${code}/read?lang=${encodeURIComponent(chosenLanguage)}`);
  };

  if (username) return <WriterDashboard />;

  return (
    <div className="homepage-container public-home">
      <section className="join-session-box" aria-labelledby="home-title">
        <h3 id="home-title">Join a Translation Session</h3>
        {error && <div className="alert alert-error" role="alert">{error}</div>}
        <form onSubmit={joinReader}>
          <label htmlFor="reader-room-code">Room Code</label>
          <input id="reader-room-code" className="form-control room-code-input" value={roomCode} onChange={(event) => setRoomCode(event.target.value.toUpperCase())} maxLength={6} autoCapitalize="characters" placeholder="Enter 6-digit code" />
          <label htmlFor="reader-language" className="custom-language-label">Choose Language</label>
          <select id="reader-language" className="form-control language-select" value={language} onChange={(event) => { setLanguage(event.target.value); setCustomLanguage(''); }} disabled={Boolean(customLanguage.trim())}>
            {LANGUAGES.map((item) => <option key={item} value={item}>{item}</option>)}
          </select>
          <label htmlFor="reader-custom-language">Or Enter Language Name</label>
          <input id="reader-custom-language" className="form-control" value={customLanguage} onChange={(event) => setCustomLanguage(event.target.value)} placeholder="e.g., Swahili (Overrides dropdown)" />
          <button type="submit" className="btn btn-primary btn-wide">Join Session</button>
        </form>
      </section>
    </div>
  );
}
